/**
 * Module « À relancer » — contrat partagé (bible §7).
 * Trois files INDÉPENDANTES, aucun score global (§7.1). Cette v1 livre la
 * mécanique commune + la file RELATION ; WORK et FINANCE suivent (§30).
 * RGPD : aucun champ patient — les actions référencent cabinet/praticien/travail.
 */
import { z } from "zod";
import type { Role } from "./roles";
import { SyncMetaSchema } from "./dto";
import { HeritageProthesisSchema } from "./finance-horizon";
import { ETAT_SAISIE_NORMAL, EtatSaisieSchema } from "./pauses";
import { IdMetier, IsoDate, Uuid } from "./primitives";
import { EchangeResumeExplanationSchema } from "./resumes";

/**
 * État de la pause de saisie Vevidental, porté par CHAQUE réponse de file.
 *
 * Quand la saisie est en pause, les compteurs de réception et les signaux de
 * silence des files comptent des choses qui n'ont pas encore été saisies : la
 * réponse doit le dire (même état que le pilotage — une seule fonction,
 * `etatSaisie`). `.default(ETAT_SAISIE_NORMAL)` : même patron que
 * `repartition` — le contrat accepte la forme AVANT que le serveur ne l'émette,
 * un front à jour reste compatible avec un serveur antérieur.
 */
const SaisieDeFile = EtatSaisieSchema.default(ETAT_SAISIE_NORMAL);

/* ------------------------------------------------------------------ enums */

export const QUEUES = ["RELATION", "WORK", "FINANCE"] as const;
export type Queue = (typeof QUEUES)[number];

/** Cycle commun d'une action (§7.4). */
export const ACTION_STATUTS = [
  "nouvelle",
  "attribuee",
  "en_cours",
  "attente_tiers",
  "resolue",
  "cloturee",
  "doublon",
  "annulee",
] as const;
export type ActionStatut = (typeof ACTION_STATUTS)[number];
export const STATUTS_ACTION_OUVERTS: readonly ActionStatut[] = [
  "nouvelle",
  "attribuee",
  "en_cours",
  "attente_tiers",
  "resolue",
];

/** Résultats obligatoires d'une tentative (§7.5). */
export const RESULTATS_TENTATIVE = [
  "joint",
  "non_joint",
  "rappel_demande",
  "email_envoye",
  "activite_reprise",
  "probleme_identifie",
  "changement_cabinet",
  "relation_terminee",
  "promesse_paiement",
  "information_recue",
  // clôtures des files Travaux/Finance (§7.B.7, §7.C.9)
  "resolu_source", // la source (Vevidental/FLOW) montre la situation résolue
  "paiement_confirme", // paiement visible dans la source — jamais « le client dit avoir payé »
  "echeancier_convenu",
  "avoir_confirme",
  "litige_transfere",
  "erreur_source_corrigee",
  "saisie_effectuee",
  // Facture antérieure à la bascule Vevidental : sa donnée de règlement vient
  // de Prothesis et n'a pas été reprise. On ne conclut RIEN sur le paiement —
  // on dit seulement que le CRM n'a pas de quoi en juger. Un code à part était
  // indispensable : « resolu_source » aurait affirmé que la source montre la
  // situation résolue, ce qui serait faux, et un futur lecteur l'aurait cru.
  "hors_horizon_migration",
  "autre",
] as const;
export type ResultatTentative = (typeof RESULTATS_TENTATIVE)[number];

/** Résultats qui CLÔTURENT l'action relationnelle (§7.A.8 « résultat ferme »). */
export const RESULTATS_CLOTURANTS: readonly ResultatTentative[] = [
  "activite_reprise",
  "relation_terminee",
  "changement_cabinet",
];

/** Niveaux de la file Relation (§7.A.4) — dérivés du ratio R, jamais éditables. */
export const NIVEAUX_RELATION = ["normal", "a_surveiller", "recommandee", "prioritaire"] as const;
export type NiveauRelation = (typeof NIVEAUX_RELATION)[number];

export function niveauRelation(ratio: number): NiveauRelation {
  if (ratio >= 3) return "prioritaire";
  if (ratio >= 2) return "recommandee";
  if (ratio >= 1.5) return "a_surveiller";
  return "normal";
}

/** Séquence des contacts injoignables (§7.A.8) — offsets calendaires depuis J0. */
export const SEQUENCE_RELANCE_JOURS = [0, 3, 10, 17, 24, 30] as const;
/** Index de l'étape d'escalade Estelle (J+30, pas de 6e appel automatique). */
export const ETAPE_ESCALADE = SEQUENCE_RELANCE_JOURS.length - 1;

export const RELATION_RULE_VERSION = "relation-inactivity/v1";
export const WORK_RULE_VERSION = "work-alerts/v1";
export const FINANCE_RULE_VERSION = "finance-overdue/v1";

/* --------------------------------------------------- file TRAVAUX (§7.B) */

/** Catégories §7.B.3 — une situation informative sans action humaine n'entre pas. */
export const CATEGORIES_WORK = [
  "element_manquant", // information, prescription, empreinte ou fichier manquant
  "validation_praticien",
  "echeance", // menacée ou dépassée
  "logistique", // anomalie ramassage/livraison
  "retour_sav",
  "attente_praticien", // le dentiste doit répondre (réveils)
] as const;
export type CategorieWork = (typeof CATEGORIES_WORK)[number];

/** Seuils §7.B.6 : critique = < 24 h ouvrées ou dépassée ; imminent = ≤ 2 j ouvrés. */
export const URGENCES_WORK = ["critique", "imminent", "normal"] as const;
export type UrgenceWork = (typeof URGENCES_WORK)[number];

/** Ordre de priorité §7.B.5 (1 = plus urgent) — le VIP ne fait que départager. */
export const ORDRE_CATEGORIE_WORK: Record<CategorieWork, number> = {
  echeance: 3,
  element_manquant: 2, // production bloquée
  validation_praticien: 2,
  retour_sav: 4,
  logistique: 5,
  attente_praticien: 6, // horizon long (jusqu'à 120 j.o.) — RACCOURCI NOMMÉ, priorité non spécifiée par le brief, à confirmer
};

/* --------------------------------------------------- file FINANCE (§7.C) */

/** Niveaux §7.C.4 (configurables) — J+1-29 reste en synthèse, hors file. */
export const NIVEAUX_FINANCE = ["a_surveiller", "relance_due", "prioritaire", "critique"] as const;
export type NiveauFinance = (typeof NIVEAUX_FINANCE)[number];

export function niveauFinance(ageJours: number): NiveauFinance | null {
  if (ageJours >= 90) return "critique";
  if (ageJours >= 60) return "prioritaire";
  if (ageJours >= 30) return "relance_due";
  if (ageJours >= 1) return "a_surveiller";
  return null;
}

export const CATEGORIES_FINANCE = [
  "seuil", // facture arrivée à seuil
  "reglement_partiel",
  "promesse_echue",
  "litige",
  "piece_manquante",
  "ecart_sources",
  "reglement_a_confirmer",
  "action_source_requise",
] as const;
export type CategorieFinance = (typeof CATEGORIES_FINANCE)[number];

/* ------------------------------------------------------------ explication */

// IsoDate / Uuid / IdMetier vivent dans `primitives.ts` : le module finance a
// fini par en avoir besoin, et les redéfinir ici aurait créé deux règles.

/**
 * Explications STRUCTURÉES (§7.7) : les valeurs + la version de règle, jamais un
 * texte figé — le front reconstruit « 42 ÷ 14 = 3 ». Union discriminée par
 * ruleVersion : une action porte l'explication de SA file.
 */
export const RelationExplanationSchema = z.object({
  ruleVersion: z.literal(RELATION_RULE_VERSION),
  silenceJours: z.number().int().min(0),
  cadenceJours: z.number().positive(),
  ratio: z.number().min(0),
  travaux6Mois: z.number().int().min(4),
  dernierTravailLe: IsoDate,
});
export type RelationExplanation = z.infer<typeof RelationExplanationSchema>;

export const WorkExplanationSchema = z.object({
  ruleVersion: z.literal(WORK_RULE_VERSION),
  categorie: z.enum(CATEGORIES_WORK),
  urgence: z.enum(URGENCES_WORK),
  workCode: z.string(),
  statutTravail: z.string().nullable(),
  echeance: IsoDate.nullable(),
  joursOuvresRestants: z.number().int().nullable(), // négatif = dépassée
  detail: z.string().nullable(), // motif retour, élément manquant…
  /** Provenance de la date (§7.B.6) : Vevidental (sync) ou « Saisie CRM ». */
  provenanceDate: z.enum(["vevidental", "saisie_crm"]).nullable(),
});
export type WorkExplanation = z.infer<typeof WorkExplanationSchema>;

export const FinanceExplanationSchema = z.object({
  ruleVersion: z.literal(FINANCE_RULE_VERSION),
  categorie: z.enum(CATEGORIES_FINANCE),
  numeroFacture: z.string(),
  statutFacture: z.string(),
  echeanceSource: IsoDate.nullable(),
  /** Âge = indicateur CRM ; montant/état officiels = la source (§7.C.4). */
  ageJours: z.number().int().min(0),
  resteDu: z.number().nullable(),
  // « via_twenty » = cartes créées avant la bascule du 11/08 (chantier 1) ;
  // les nouvelles lisent la copie API directement. Les deux restent lisibles.
  source: z.enum(["vevidental_via_twenty", "vevidental_api"]),
});
export type FinanceExplanation = z.infer<typeof FinanceExplanationSchema>;

/**
 * Ressaisie Vevidental (décision n° 9 du 27/08, construite le 01/09) : le CRM
 * ne peut pas ÉCRIRE dans Vevidental — un patient né au CRM doit être retapé
 * là-bas. Chaque patient inconnu de la source devient une action gouvernée ;
 * elle se résout SEULE quand le patient apparaît dans la copie Vevidental.
 */
export const RESSAISIE_RULE_VERSION = "ressaisie-vevidental/v1" as const;
export const RessaisieExplanationSchema = z.object({
  ruleVersion: z.literal(RESSAISIE_RULE_VERSION),
  patientId: Uuid,
  patientNom: z.string().min(1),
  /** Code de la clinique dans Vevidental — null si le cabinet n'y est pas identifié. */
  cabinetVevidentalCode: z.string().nullable(),
  creeLe: IsoDate,
  creePar: z.string(),
});
export type RessaisieExplanation = z.infer<typeof RessaisieExplanationSchema>;

export const ActionExplanationSchema = z.discriminatedUnion("ruleVersion", [
  RelationExplanationSchema,
  WorkExplanationSchema,
  FinanceExplanationSchema,
  RessaisieExplanationSchema,
  // L2 (07/09) : une action née d'une proposition IA sur un échange
  EchangeResumeExplanationSchema,
]);
export type ActionExplanation = z.infer<typeof ActionExplanationSchema>;

/** Tous les niveaux possibles, toutes files confondues (uniques — la base stocke du texte). */
export const NIVEAUX_ACTION = [
  "normal",
  "a_surveiller",
  "recommandee",
  "prioritaire",
  "imminent",
  "critique",
  "relance_due",
] as const;
export type NiveauAction = (typeof NIVEAUX_ACTION)[number];

/* ------------------------------------------------------------- historique */

export const ActionEventSchema = z.object({
  at: z.string().datetime({ offset: true }),
  type: z.enum([
    "creation",
    "detection_maj",
    "attribution",
    "tentative",
    "report",
    "escalade",
    "confirmation", // saisie Vevidental effectuée (§7.B.7)
    "cloture",
  ]),
  acteur: z.string(), // email ou "systeme"
  resultat: z.enum(RESULTATS_TENTATIVE).nullable(),
  commentaire: z.string().nullable(),
  detail: z.string().nullable(), // ex. code du travail déclencheur d'une reprise
});
export type ActionEvent = z.infer<typeof ActionEventSchema>;

/* ------------------------------------------------------------- ActionCase */

export const ActionCaseSchema = z.object({
  id: Uuid,
  queue: z.enum(QUEUES),
  category: z.string(), // "inactivite", catégories §7.B.3 / §7.C.5
  status: z.enum(ACTION_STATUTS),
  niveau: z.enum(NIVEAUX_ACTION),
  explanation: ActionExplanationSchema,
  workId: IdMetier.nullable(),
  invoiceId: IdMetier.nullable(),
  /** §7.B.7 : reste au maximum Résolue tant que la saisie n'est pas confirmée. */
  saisieVevidentalRequise: z.boolean(),
  saisieConfirmee: z.boolean(),
  /** §2.4 : QUOI saisir dans Vevidental (posé à la demande). */
  saisieDescription: z.string().nullable(),
  /** §31 : PREUVE apportée à la confirmation — obligatoire pour clôturer. */
  saisiePreuve: z.string().nullable(),
  cabinetId: Uuid,
  cabinetNom: z.string(),
  practitionerId: IdMetier.nullable(),
  practitionerNom: z.string().nullable(),
  affiliationKey: z.string().nullable(),
  assignee: z.string().nullable(),
  /** Étape de séquence atteinte : 0 = J0 … 5 = J+30 (escalade Estelle). */
  sequenceStep: z.number().int().min(0).max(ETAPE_ESCALADE),
  /** Date de règle et date après décalage ouvré — TOUTES DEUX conservées (§7.6). */
  theoreticalDueAt: IsoDate.nullable(),
  effectiveDueAt: IsoDate.nullable(),
  snoozeUntil: IsoDate.nullable(),
  snoozeReason: z.string().nullable(),
  nextActionAt: IsoDate.nullable(),
  resolutionCode: z.enum(RESULTATS_TENTATIVE).nullable(),
  enRetard: z.boolean(),
  escalade: z.boolean(),
  /**
   * Sprint 2 : l'action est née pendant une pause de saisie (ou son échéance
   * théorique tombe dedans) — calculée à la lecture, `.default(false)` pour
   * que les réponses d'avant le sprint 2 passent telles quelles.
   */
  detecteePendantPause: z.boolean().default(false),
  createdAt: z.string().datetime({ offset: true }),
  closedAt: z.string().datetime({ offset: true }).nullable(),
  previousCaseId: Uuid.nullable(),
  history: z.array(ActionEventSchema),
});
export type ActionCase = z.infer<typeof ActionCaseSchema>;

/* ------------------------------------------------- synthèse « à surveiller » */

/** Rattachement 1,5 ≤ R < 2 : visible dans la synthèse, HORS file (§7.A.4). */
export const SurveillanceItemSchema = z.object({
  cabinetId: Uuid,
  cabinetNom: z.string(),
  practitionerId: IdMetier,
  practitionerNom: z.string().nullable(),
  explanation: RelationExplanationSchema,
});
export type SurveillanceItem = z.infer<typeof SurveillanceItemSchema>;

/* ---------------------------------------------------------------- réponse */

/**
 * Actions clôturées AUJOURD'HUI — renvoyées à part des actions ouvertes.
 *
 * Sans elles, la puce « clôturées aujourd'hui » affichait un nombre que le clic
 * ne pouvait ouvrir : les actions comptées n'étaient dans la réponse à aucun
 * titre. Un compteur sans ses éléments est un compteur invérifiable.
 */
const ClotureesDuJour = z.array(ActionCaseSchema);

/** Actions reportées, volontairement masquées : leur NOMBRE reste affiché. */
const ReporteesMasquees = z.number().int().min(0);

export const FileRelationResponseSchema = z.object({
  items: z.array(ActionCaseSchema),
  clotureesDuJour: ClotureesDuJour,
  reporteesMasquees: ReporteesMasquees,
  surveilles: z.array(SurveillanceItemSchema),
  kpi: z.object({
    aTraiter: z.number().int().min(0),
    enRetard: z.number().int().min(0),
    critiques: z.number().int().min(0), // niveau prioritaire (R ≥ 3)
    attenteTiers: z.number().int().min(0),
    escalades: z.number().int().min(0),
    clotureesAujourdhui: z.number().int().min(0),
  }),
  detection: z.object({
    lastRunAt: z.string().datetime({ offset: true }).nullable(),
    rattachementsEvalues: z.number().int().min(0),
    historiqueInsuffisant: z.number().int().min(0),
    travauxSansPraticien: z.number().int().min(0), // honnêteté : jamais masqué
    /**
     * Créations retenues par la passe pendant une pause de saisie (ou son
     * rattrapage). Le moteur les comptait déjà ; la route les JETAIT — un
     * compte tenu côté serveur mais invisible à l'écran est un compte tu.
     * `.default(0)` : rétro-compatible, comme `saisie` ci-dessous.
     */
    ignoreesPause: z.number().int().min(0).default(0),
  }),
  /**
   * Actions mises de côté par l'amnistie de dette héritée (0 si aucune
   * amnistie). AFFICHÉ, jamais silencieux : une dette qu'on masque sans la
   * compter est une dette qu'on oublie.
   */
  heritees: z.number().int().min(0).default(0),
  saisie: SaisieDeFile,
  // LE schéma partagé, pas une copie de sa forme : cette page recopiait les
  // trois champs, whatsapp.ts aussi, et l'ajout d'un quatrième (l'âge réel de
  // la copie) aurait dû être fait à trois endroits — donc oublié à un.
  sync: SyncMetaSchema,
});
export type FileRelationResponse = z.infer<typeof FileRelationResponseSchema>;

/* ------------------------------------------- réponses files Travaux/Finance */

const SyncSchema = SyncMetaSchema;

export const FileTravauxResponseSchema = z.object({
  items: z.array(ActionCaseSchema),
  clotureesDuJour: ClotureesDuJour,
  reporteesMasquees: ReporteesMasquees,
  kpi: z.object({
    aTraiter: z.number().int().min(0),
    critiques: z.number().int().min(0),
    imminents: z.number().int().min(0),
    attenteTiers: z.number().int().min(0),
    saisiesVevidentalAttendues: z.number().int().min(0),
    clotureesAujourdhui: z.number().int().min(0),
  }),
  detection: z.object({
    lastRunAt: z.string().datetime({ offset: true }).nullable(),
    travauxEnCoursEvalues: z.number().int().min(0),
    sansEcheance: z.number().int().min(0), // jamais comptés en dépassés — affiché
  }),
  heritees: z.number().int().min(0).default(0),
  saisie: SaisieDeFile,
  sync: SyncSchema,
});
export type FileTravauxResponse = z.infer<typeof FileTravauxResponseSchema>;

/** Synthèse J+1-29 : visible, HORS file (§7.C.4). */
export const FinanceSurveilleSchema = z.object({
  // `IdMetier` et non `Uuid` : une facture vient de Vevidental, qui la numérote
  // en entier. Ce champ était resté en arrière lors de la correction du 11/08 —
  // il refusait la réponse ENTIÈRE de la file dès qu'une facture était entre
  // J+1 et J+29. Cf. le test dédié dans `contrat-files.test.ts`.
  factureId: IdMetier,
  numeroFacture: z.string(),
  cabinetId: Uuid.nullable(),
  cabinetNom: z.string(),
  ageJours: z.number().int().min(0),
  resteDu: z.number().nullable(),
});
export type FinanceSurveille = z.infer<typeof FinanceSurveilleSchema>;

export const FileFinanceResponseSchema = z.object({
  items: z.array(ActionCaseSchema),
  clotureesDuJour: ClotureesDuJour,
  reporteesMasquees: ReporteesMasquees,
  surveilles: z.array(FinanceSurveilleSchema),
  kpi: z.object({
    aTraiter: z.number().int().min(0),
    critiques: z.number().int().min(0), // J+90 et plus
    prioritaires: z.number().int().min(0),
    promessesEnCours: z.number().int().min(0),
    attenteTiers: z.number().int().min(0),
    clotureesAujourdhui: z.number().int().min(0),
  }),
  detection: z.object({
    lastRunAt: z.string().datetime({ offset: true }).nullable(),
    facturesEvaluees: z.number().int().min(0),
  }),
  heritees: z.number().int().min(0).default(0),
  saisie: SaisieDeFile,
  /**
   * Ce que l'horizon de bascule écarte de la file — AFFICHÉ, jamais tu.
   * Une dette qu'on masque sans la compter est une dette qu'on oublie.
   */
  heritage: HeritageProthesisSchema,
  sync: SyncSchema,
});
export type FileFinanceResponse = z.infer<typeof FileFinanceResponseSchema>;

/* ------------------------------------------------------------------- RBAC */

/** Matrice §4.3 « Traiter relance relationnelle » : Dove/Estelle/Élodie oui, comptable non. */
export const ROLES_TRAITER_RELANCE: readonly Role[] = ["admin", "secretariat", "logistique"];

export function canTraiterRelance(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_TRAITER_RELANCE.includes(r));
}

/** §7.B.2 : Élodie échanges/suivi, Estelle arbitrage, Dove supervision. */
export const ROLES_TRAITER_TRAVAUX: readonly Role[] = ["admin", "secretariat", "logistique", "production"];

export function canTraiterTravaux(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_TRAITER_TRAVAUX.includes(r));
}

/** §7.C.3 : comptable principal, Estelle coordination, Élodie collecte, Dove supervision. */
export const ROLES_TRAITER_FINANCE: readonly Role[] = ["admin", "secretariat", "comptabilite", "logistique"];

export function canTraiterFinance(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_TRAITER_FINANCE.includes(r));
}
